import { getCurrentUserProfileInfos } from "@api/core-api-utils";
import { faroCoreApiClient } from "@api/faro-core-api-client";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { RootState } from "@store/store-helper";
import { createSplashScreenPayload } from "@store/user/user-slice-helper";
import { UpdateCurrentUserProfileProps } from "@store/user/user-slice-types";

/**
 * Fetches information of current logged in user
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const fetchCurrentUser = createAsyncThunk<
  FaroCoreApiTypes.CompanyMemberBase,
  BaseCompanyIdProps,
  { state: RootState }
>("user/fetchCurrentUser", async ({ companyId }, { getState }) => {
  const {
    app: { isDevModeEnabled },
  } = getState();
  return await getCurrentUserProfileInfos(companyId, isDevModeEnabled);
});

/**
 * Set the FueFlags (First User Experience). These flags determine which introduction (in the form of splash screen)
 * the user has seen so far. The provided payload will be set as FUE flags directly.
 * It will not be added to the existed FUE flags in backend
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const setFueFlags = createAsyncThunk<
  {
    updatedValue: string;
  },
  void,
  { state: RootState }
>("user/setFueFlags", async (_, { getState }) => {
  const {
    user: { splashScreens },
  } = getState();
  const fueFlags = createSplashScreenPayload(splashScreens);
  return await faroCoreApiClient.users.setUserFueFlags(fueFlags);
});

/**
 * Logs out the current user by calling the logout route. Does not take care of redirection afterwards
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const logoutUser = createAsyncThunk<void, void>(
  "user/logoutUser",
  async (): Promise<void> => {
    await faroCoreApiClient.users.logoutUser();
  }
);

/**
 * Update information of current logged in user
 * @throws {Error} if the information to update profile is incomplete
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const updateCurrentUserProfile = createAsyncThunk<
  FaroCoreApiTypes.UserDetails,
  UpdateCurrentUserProfileProps
>("user/updateCurrentUserProfile", async (payload) => {
  if (!payload.entity) {
    throw new Error("The information to update profile is incomplete");
  }
  const data = await faroCoreApiClient.users.setMetaUserProfile(
    payload.entity
  );
  return data;
});

/**
 * Fetches information of current logged in user, extra details like login method, SSO provider
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const getLoggedInUser = createAsyncThunk<
  FaroCoreApiTypes.LoggedInUser,
  void,
  {
    state: RootState;
  }
>("user/getLoggedInUser", async () => {
  return await faroCoreApiClient.users.getLoggedInUser();
});
